
import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X, Plus } from 'lucide-react';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LANGUAGES } from "@/data/constants";

interface LanguagesFormProps {
  languages: string[] | null;
  newLanguage: string;
  onNewLanguageChange: (value: string) => void;
  onAddLanguage: () => void;
  onRemoveLanguage: (index: number) => void;
}

const LanguagesForm = ({
  languages,
  newLanguage,
  onNewLanguageChange,
  onAddLanguage,
  onRemoveLanguage
}: LanguagesFormProps) => {
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [customLanguage, setCustomLanguage] = useState<string>('');
  const [isCustomLanguage, setIsCustomLanguage] = useState<boolean>(false);
  
  // Filter out already selected languages
  const availableLanguages = React.useMemo(() => {
    if (!languages) return LANGUAGES;
    return LANGUAGES.filter(lang => !languages.includes(lang.label));
  }, [languages]);
  
  const handleAddLanguage = () => {
    if (isCustomLanguage) {
      if (customLanguage.trim()) {
        onNewLanguageChange(customLanguage.trim());
        onAddLanguage();
        setCustomLanguage('');
      }
    } else {
      if (selectedLanguage) {
        onNewLanguageChange(selectedLanguage);
        onAddLanguage();
        setSelectedLanguage('');
      }
    }
  };
  
  const toggleCustomLanguage = () => {
    setIsCustomLanguage(!isCustomLanguage);
    setSelectedLanguage('');
    setCustomLanguage('');
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">Languages</h3>
      
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2 mb-2">
          {languages && languages.map((language, index) => (
            <Badge key={index} variant="secondary" className="px-3 py-1">
              {language}
              <button 
                type="button" 
                onClick={() => onRemoveLanguage(index)}
                className="ml-2"
              >
                <X className="h-3 w-3" />
                <span className="sr-only">Remove {language}</span>
              </button>
            </Badge>
          ))}
        </div>
        
        <div className="flex gap-2">
          {isCustomLanguage ? (
            <Input
              value={customLanguage}
              onChange={(e) => setCustomLanguage(e.target.value)}
              placeholder="Type custom language"
              className="flex-1"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddLanguage();
                }
              }}
            />
          ) : (
            <Select
              value={selectedLanguage}
              onValueChange={setSelectedLanguage}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a language" />
              </SelectTrigger>
              <SelectContent>
                <div className="max-h-[300px] overflow-y-auto">
                  {availableLanguages.map((language) => (
                    <SelectItem key={language.id} value={language.label}>
                      {language.label}
                    </SelectItem>
                  ))}
                </div>
              </SelectContent>
            </Select>
          )}
          
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleAddLanguage}
            disabled={isCustomLanguage ? !customLanguage : !selectedLanguage}
          >
            Add
          </Button>
          
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={toggleCustomLanguage}
            title={isCustomLanguage ? "Use predefined languages" : "Add custom language"}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LanguagesForm;
